import {
  DateTypeFrontend,
  DateTypeBackend,
} from '@/pages/intelligence/pages/TopCharts/_config/enums';
import ConsoleGameDefault from '@/pages/intelligence/assets/imgs/common/consolegame-default.png';
import GameDefault from '@/pages/intelligence/assets/imgs/common/game-default.svg';
import PcGameDefault from '@/pages/intelligence/assets/imgs/common/pcgame-default.png';
import CompanyDefault from '@/pages/intelligence/assets/imgs/common/company-default.svg';
import moment from 'moment';
import {
  analysisPlatformsByString,
  analysisPlatformsBasicByString,
} from '@intelligence/utils/topchartsUtils';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import type { IDateType, IDateTypely } from '../types/common';
import type { IMenuNewItemType } from '@/pages/intelligence/types/common';
import { isEmpty } from '@/pages/intelligence/utils/commonUtils';

// 数据地图跳转路径 --- 可引入home目录的，因为系统发布不同步，先在情报这里也加上
export const datamapOverviewPath = '/intelligence/sourcesMetrics/overview';
export const datamapMobileGameFieldsPath = '/intelligence/sourcesMetrics/metrics/MobileGameFields';
export const datamapPCConsoleFieldsPath = '/intelligence/sourcesMetrics/metrics/PCConsoleFields';
export const datamapIntelligenceSourcesPath =
  '/intelligence/sourcesMetrics/sources/IntelligenceSources';
export const datamapIntelligenceMetricsPath =
  '/intelligence/sourcesMetrics/metrics/IntelligenceMetrics';
export const datamapOpinionsMetricsPath = '/intelligence/sourcesMetrics/metrics/OpinionsMetrics';

// 所有日期类型格式转换成ly格式
export const toDateTypeLy: { [key: string]: any } = {
  day: 'daily',
  date: 'daily',
  week: 'weekly',
  month: 'monthly',
  Month: 'monthly',
  Week: 'weekly',
  Day: 'daily',
  Date: 'daily',
  monthly: 'monthly',
  weekly: 'weekly',
  daily: 'daily',
  year: 'yearly',
  Year: 'yearly',
  yearly: 'yearly',
  Yearly: 'yearly',
  hour: 'hourly',
  Hour: 'hourly',
  hourly: 'hourly',
  Hourly: 'hourly',
} as any;

// 所有日期类型格式转换成正常格式
export const toDateTypeNor: { [key: string]: any } = {
  day: 'day',
  date: 'day',
  week: 'week',
  month: 'month',
  Month: 'month',
  Week: 'week',
  Day: 'day',
  Date: 'day',
  monthly: 'month',
  weekly: 'week',
  daily: 'day',
  year: 'year',
  Year: 'year',
  yearly: 'year',
  Yearly: 'year',
  hour: 'hour',
  Hour: 'hour',
  hourly: 'hour',
  Hourly: 'hour',
};

// 继承-日期类型格式转换成正常格式
export const toDateTypeFilter: any = {
  day: 'date',
  date: 'date',
  week: 'week',
  month: 'month',
  Month: 'month',
  Week: 'week',
  Day: 'date',
  Date: 'date',
  monthly: 'month',
  weekly: 'week',
  daily: 'date',
  year: 'year',
  Year: 'year',
  yearly: 'year',
  Yearly: 'year',
  hour: 'hour',
  Hour: 'hour',
  hourly: 'hour',
  Hourly: 'hour',
} as any;

// 通过日期粒度返回moment日期格式
export const toDateTypeMomentFormat: { [key: string]: any } = {
  day: 'YYYY-MM-DD',
  date: 'YYYY-MM-DD',
  daily: 'YYYY-MM-DD',
  Day: 'YYYY-MM-DD',
  Date: 'YYYY-MM-DD',
  Daily: 'YYYY-MM-DD',
  week: 'YYYY-MM-DD',
  Week: 'YYYY-MM-DD',
  weekly: 'YYYY-MM-DD',
  Weekly: 'YYYY-MM-DD',
  month: 'YYYY-MM',
  Month: 'YYYY-MM',
  monthly: 'YYYY-MM',
  Monthly: 'YYYY-MM',
  year: 'YYYY',
  Year: 'YYYY',
  yearly: 'YYYY',
  Yearly: 'YYYY',
  hour: 'YYYY-MM-DD HH:mm',
  Hour: 'YYYY-MM-DD HH:mm',
  hourly: 'YYYY-MM-DD HH:mm',
  Hourly: 'YYYY-MM-DD HH:mm',
};

// 通过日期返回Active Users对应指标名
export const toActiveUsersTag: { [key: string]: any } = {
  day: 'DAU',
  week: 'WAU',
  month: 'MAU',
  Month: 'MAU',
  Week: 'WAU',
  Day: 'DAU',
  monthly: 'MAU',
  weekly: 'WAU',
  daily: 'DAU',
} as any;

// 通过日期返回Active Users对应指标名
export const toActiveUsersTagToUpperFirstCase: { [key: string]: any } = {
  day: 'Dau',
  week: 'Wau',
  month: 'Mau',
  Month: 'Mau',
  Week: 'Wau',
  Day: 'Dau',
  monthly: 'Mau',
  weekly: 'Wau',
  daily: 'Dau',
} as any;

// 通过日期返回Active Users对应指标名
export const toActiveUsersTagToCN: { [key: string]: any } = {
  day: '日活用户',
  week: '周活用户',
  month: '月活用户',
  Month: '月活用户',
  Week: '周活用户',
  Day: '日活用户',
  monthly: '月活用户',
  weekly: '周活用户',
  daily: '日活用户',
} as any;

// 通过日期返回Active Users对应指标名--中文
export const toActiveUsersTagCN: { [key: string]: any } = {
  day: '日活跃玩家数',
  week: '周活跃玩家数',
  month: '月活跃玩家数',
  Month: '月活跃玩家数',
  Week: '周活跃玩家数',
  Day: '日活跃玩家数',
  monthly: '月活跃玩家数',
  weekly: '周活跃玩家数',
  daily: '日活跃玩家数',
} as any;

// 通过游戏类型返回默认overview时间粒度
export const toOverviewDateType: { [key: string]: any } = {
  mobile: 'day',
  pc: 'month',
  console: 'month',
  combine: 'month',
} as any;

/*
 * @Description: 时间筛选器-日期相关
 */
export const TIMETYPETOLY: { [key: string]: any } = {
  monthly: 'Month',
  weekly: 'Week',
  daily: 'Day',
  month: 'Month',
  week: 'Week',
  day: 'Day',
  Month: 'Month',
  Week: 'Week',
  Day: 'Day',
} as any;

export const DEFAULT_RELEASE_TIME_VALUE = {
  date: ['1', '30'],
  week: ['1', '4'],
  month: ['1', '6'],
} as any;

const dateType2FrontendMap = {
  [DateTypeBackend.Day]: DateTypeFrontend.Day,
  [DateTypeBackend.Week]: DateTypeFrontend.Week,
  [DateTypeBackend.Month]: DateTypeFrontend.Month,
};
export const getDateTypeOfFrontend = (value: DateTypeBackend): DateTypeFrontend =>
  dateType2FrontendMap[value] || '';

const dateType2BackendMap = {
  [DateTypeFrontend.Day]: DateTypeBackend.Day,
  [DateTypeFrontend.Week]: DateTypeBackend.Week,
  [DateTypeFrontend.Month]: DateTypeBackend.Month,
};
export const getDateTypeOfBackend = (value: DateTypeFrontend): DateTypeBackend =>
  dateType2BackendMap[value] || value;

export const isSelectionEquals = (selectionA: string[], selectionB: string[]): boolean => {
  if (selectionA?.length !== selectionB?.length) return false;
  return selectionA?.every((item) => selectionB?.includes(item));
};

/** 时间筛选器转换格式为date, week, month */
export const feachTimeTypeToFilterTimeType = (type: string) => {
  if (typeof type !== 'string') {
    return type;
  }
  const feachType = type?.toLocaleLowerCase();

  switch (feachType) {
    case 'day':
    case 'daily':
      return 'date'; // 注意是date, 不是day
    case 'weekly':
    case 'week':
      return 'week';
    case 'monthly':
    case 'month':
      return 'month';
    case 'quarterly':
      return 'quarter';
    case 'lifetime':
      return 'lifetime';
    case 'year':
    case 'yearly':
      return 'year';
    case 'hour':
    case 'hourly':
      return 'hour';
    default:
      return type;
  }
};

const dateTypeTransformMap: {
  [key in IDateType]: IDateTypely;
} = {
  date: 'daily',
  week: 'weekly',
  month: 'monthly',
  year: 'yearly',
};
export function transformDateType2dateTypely(dateType: IDateType): IDateTypely {
  return dateTypeTransformMap[dateType] || dateType;
}

// 全局默认时间
export const COMPONENT_DEFAULT_TIME = {
  // 6个月， 6周， 30天
  date: [
    moment().subtract(31, 'day').format('YYYY-MM-DD'),
    moment().subtract(1, 'day').format('YYYY-MM-DD'),
  ],
  week: [
    moment().subtract(7, 'week').weekday(6).format('YYYY-MM-DD'),
    moment().subtract(1, 'week').weekday(5).format('YYYY-MM-DD'),
  ],
  month: [
    moment().subtract(6, 'month').endOf('month').format('YYYY-MM-DD'),
    moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'),
  ],
} as any;

export const getDefulatTimeValueByDateType = (dateType: string) => {
  return COMPONENT_DEFAULT_TIME[toDateTypeFilter[dateType]] || ['', ''];
};

export const DEFAULT_DATA_TIME_VALUE_DATE = {
  date_type: 'daily',
  start_date: COMPONENT_DEFAULT_TIME[toDateTypeFilter.daily][0],
  end_date: COMPONENT_DEFAULT_TIME[toDateTypeFilter.daily][1],
};
export const DEFAULT_DATA_TIME_VALUE_WEEK = {
  date_type: 'weekly',
  start_date: COMPONENT_DEFAULT_TIME[toDateTypeFilter.weekly][0],
  end_date: COMPONENT_DEFAULT_TIME[toDateTypeFilter.weekly][1],
};
export const DEFAULT_DATA_TIME_VALUE_MONTH = {
  date_type: 'monthly',
  start_date: COMPONENT_DEFAULT_TIME[toDateTypeFilter.monthly][0],
  end_date: COMPONENT_DEFAULT_TIME[toDateTypeFilter.monthly][1],
};

export const DEFAULT_DATA_TIME_VALUE = [
  DEFAULT_DATA_TIME_VALUE_DATE,
  DEFAULT_DATA_TIME_VALUE_WEEK,
  DEFAULT_DATA_TIME_VALUE_MONTH,
];

export const getDefulatTimeValue = (data: any) => {
  let dataValueList = data || [];
  if (isEmpty(dataValueList)) {
    dataValueList = DEFAULT_DATA_TIME_VALUE;
  } else {
    let dateValue = dataValueList?.find((item: any) => item?.date_type === 'daily') || {};
    if (isEmpty(dateValue) || isEmpty(dateValue?.start_date) || isEmpty(dateValue?.end_date)) {
      dateValue = DEFAULT_DATA_TIME_VALUE_DATE;
    }
    let weekValue = dataValueList?.find((item: any) => item?.date_type === 'weekly') || {};
    if (isEmpty(weekValue) || isEmpty(weekValue?.start_date) || isEmpty(weekValue?.end_date)) {
      weekValue = DEFAULT_DATA_TIME_VALUE_WEEK;
    }
    let monthValue = dataValueList?.find((item: any) => item?.date_type === 'monthly') || {};
    if (isEmpty(monthValue) || isEmpty(monthValue?.start_date) || isEmpty(monthValue?.end_date)) {
      monthValue = DEFAULT_DATA_TIME_VALUE_MONTH;
    }
    dataValueList = [dateValue, weekValue, monthValue];
  }

  return dataValueList;
};

// 获取匹配的国家图片地址公共方法
export const getCountryImg = (str: string, type = 'default') => GameDefault;

// 根据游戏类型获取不同默认图片
export const getDefaultImg = (type: string) => {
  switch (type) {
    case 'mobile':
      return GameDefault;
    case 'pc':
      return PcGameDefault;
    case 'console':
      return ConsoleGameDefault;
    case 'company':
      return CompanyDefault;
    default:
      return GameDefault;
  }
};

// 获取是否是要展示百分比的指标
export const getIfPercentage = (tip: any) => {
  switch (tip) {
    case 'Player Share':
    case 'Stickiness':
    case 'Churn %':
    case 'Acquisition %':
    case 'Retention D1':
    case 'Retention D2':
    case 'Retention D4':
    case 'Retention D7':
    case 'Retention D14':
    case 'Retention D28':
    case 'Time Spent 0 - 1 hrs':
    case 'Time Spent 1 - 5 hrs':
    case 'Time Spent 5 - 10 hrs':
    case 'Time Spent 10 - 25 hrs':
    case 'Time Spent 25 - 50 hrs':
    case 'Time Spent > 50 hrs':
    case 'player_share':
    case 'time_spent_0H':
    case 'time_spent_1H':
    case 'time_spent_5H':
    case 'time_spent_10H':
    case 'time_spent_25H':
    case 'time_spent_50H':
    case 'stickiness':
    case 'churn':
    case 'acquisition':
    case 'retention_d1':
    case 'retention_d2':
    case 'retention_d4':
    case 'retention_d7':
    case 'retention_d14':
    case 'retention_d28':
    case 'cross_app_usage':
    case 'Steam Added Positive%':
    case 'Steam All reviews Positive%':
    case 'ampere_d1':
    case 'ampere_d7':
    case 'ampere_d14':
    case 'ampere_d28':
    case 'ampere_d60':
    case 'ampere_rolling_d1':
    case 'ampere_rolling_d7':
    case 'ampere_rolling_d14':
    case 'ampere_rolling_d28':
    case 'ampere_rolling_d60':
    case 'ampere_stickiness':
    case 'ampere_acquisition_rate':
      return true;
    default:
      return false;
  }
};

/**
 * metric 判断指标在 KPITrends By Platform 拆分折线图时，tooltip 中是否展示 all 合计值
 * 以下列表中 ampere 占比相关数据不展示
 */
export const isTooltipShowAll = (metric: string) =>
  ![
    'ampere_d1',
    'ampere_d7',
    'ampere_d14',
    'ampere_d28',
    'ampere_d60',
    'ampere_rolling_d1',
    'ampere_rolling_d7',
    'ampere_rolling_d14',
    'ampere_rolling_d28',
    'ampere_rolling_d60',
    'ampere_acquisition_rate',
    'ampere_stickiness',
  ].includes(metric);

// 获取是否是要*100的指标
// export const getIfMultiplyBy100 = (tip: any) => {
//   switch (tip) {
//     case 'ampere_d1':
//     case 'ampere_d7':
//     case 'ampere_d14':
//     case 'ampere_d28':
//     case 'ampere_d60':
//     case 'ampere_rolling_d1':
//     case 'ampere_rolling_d7':
//     case 'ampere_rolling_d14':
//     case 'ampere_rolling_d28':
//     case 'ampere_rolling_d60':
//       return true;
//     default:
//       return false;
//   }
// };

/**
 * 时间转换
 * @param date
 * @param pattern 转换的格式
 * @return string
 */
export const getValidDate = (date: string, pattern: string): string =>
  moment(date).isValid() && date ? moment(date).format(pattern) : date || '-';

/**
 * 从数组中删除指定元素
 * @param array
 * @param val 指定元素
 * @return []
 */
export const arrayRemoveElement = (array: [], val: any) => array.filter((item) => item != val);

export const getMetricTitle = (pageId: string, dataType: string) => {
  if (pageId === 'activeusers') {
    return toActiveUsersTag[dataType];
  }
  if (pageId === 'revenue') {
    return 'Revenue';
  }
  if (pageId === 'downloads') {
    return 'Downloads';
  }
};

export const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  return /safari/.test(ua) && !/chrome/.test(ua);
};

// 原生js实现复制内容到剪切板，兼容pc、移动端（支持Safari浏览器）
export const copyText = (
  text: string,
  options: {
    onSuccess: () => void;
    onError: () => void;
  },
) => {
  const { onSuccess, onError } = options || {};
  try {
    if (typeof document.execCommand !== 'function') {
      onError?.();
      return;
    }
    const dom = document.createElement('textarea');
    dom.value = text;
    dom.setAttribute('style', 'display: block;width: 1px; height: 1px;');
    document.body.appendChild(dom);
    dom.select();
    const result = document.execCommand('copy');
    document.body.removeChild(dom);
    if (result) {
      onSuccess?.();
      return;
    }
    if (typeof document.createRange !== 'function') {
      onError?.();
      return;
    }
    const range = document.createRange();
    const div = document.createElement('div');
    div.innerHTML = text;
    div.setAttribute('style', 'height: 1px;fontSize: 1px; overflow: hidden;');
    document.body.appendChild(div);
    range.selectNode(div);
    const selection = window.getSelection();
    if (selection?.rangeCount && selection?.rangeCount > 0) {
      selection?.removeAllRanges();
    }
    selection?.addRange(range);
    document.execCommand('copy');
    onSuccess?.();
  } catch (exception) {
    onError?.();
  }
};

export const importScript = (
  src: string,
  cfg?: {
    // key 用于同时动态加载多个script的时，给每个script一个唯一标识，重新加载时，会删除上一次的script标签
    key: string;
    // 是否异步加载
    async?: boolean;
    // 加载成功后的回调
    callback?: (e: Event) => void;
  },
) => {
  if (!src) return;
  const { key, async = true, callback } = cfg || {};
  const head = document.head || document.getElementsByTagName('head')[0];
  const prevScript = document.querySelector(`#dynamic-import-script-${key}`);
  if (prevScript) {
    prevScript.remove();
  }
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = async;
  script.src = src;
  script.id = `dynamic-import-script-${key}`;

  if ((script as any).readyState) {
    // IE
    (script as any).attachEvent('onreadystatechange', (e: Event) => {
      if (['complete', 'loaded'].includes((script as any).readyState)) {
        callback?.(e);
      }
    });
  } else {
    script.addEventListener(
      'load',
      (e: Event) => {
        callback?.(e);
      },
      false,
    );
  }

  head.appendChild(script);
};

export const getPlatformIcons = (strVal: string, isNeedMobile = false) => {
  if (typeof strVal !== 'string') {
    return '';
  }
  const str = strVal?.toLocaleLowerCase();
  const strValue = str === 'ps' ? 'playstation' : str;
  const platformIcon = isNeedMobile
    ? analysisPlatformsBasicByString(strValue)
    : analysisPlatformsByString(strValue);
  return getDataSourceIcon(platformIcon?.[0] === 'pc' ? 'pcdevice' : platformIcon?.[0]);
};
export const iconSortFun = (sourceArray: string[], orderArray: string[]) => {
  sourceArray.sort((a, b) => {
    // 使用order.indexOf找到a和b在order数组中的索引
    const indexA = orderArray.indexOf(typeof a === 'string' ? a.toLocaleLowerCase() : '');
    const indexB = orderArray.indexOf(typeof b === 'string' ? b.toLocaleLowerCase() : '');

    // 如果a不在order中，则将其放在最后
    if (indexA === -1) return 1;

    // 如果b不在order中，则将其放在a之前
    if (indexB === -1) return -1;

    // 如果a和b都在order中，则按照order中的顺序进行排序
    return indexA - indexB;
  });
  return sourceArray;
};

/** 如果有epic/steam则不展示pc */
export const filterPlatformIcons = (icons: string[]) => {
  if (!Array.isArray(icons)) {
    return [];
  }
  if (!icons.every((item) => typeof item === 'string')) {
    return [];
  }

  const lowerIcons = icons.map((t) => typeof t === 'string' ? t.toLocaleLowerCase() : '');
  let newPlatformIcons = lowerIcons;

  if (lowerIcons.includes('epic') || lowerIcons.includes('steam')) {
    newPlatformIcons = lowerIcons.filter((t) => t !== 'pc');
  }
  const platformOrder = ['ios', 'android', 'steam', 'epic', 'pc', 'ps', 'xbox', 'nintendo'];
  newPlatformIcons = iconSortFun(newPlatformIcons, platformOrder);

  return newPlatformIcons;
};

export const getPlatformIconsImg = (icons: string[]) => {
  if (!Array.isArray(icons)) {
    return [];
  }
  if (!icons.every((item) => typeof item === 'string')) {
    return [];
  }
  const newIcons = filterPlatformIcons(icons);
  return newIcons?.map((t) => (t === 'pc' ? 'pcdevice' : getPlatformIcons(t)));
};

// 能用高亮方法，返回匹配高亮字段与数量
export const getREGHeightObj = (strTemp: any, keyWord: string) => {
  // 使用正则表达式的全局搜索标志match方法,计算高亮后数据与个数
  const itemUpper = strTemp.toUpperCase();
  if (itemUpper.indexOf(keyWord.toUpperCase()) != -1) {
    const replaceWord = strTemp.substr(itemUpper.indexOf(keyWord.toUpperCase()), keyWord.length);
    const replaceHtml = `<span style="color:#326ff0">${replaceWord}</span>`;
    const regExp = new RegExp(replaceWord, 'gi');
    return {
      txt: strTemp.replace(regExp, replaceHtml),
      num: strTemp.match(regExp)?.length || 0,
    };
  }
  return {
    txt: strTemp,
    num: 0,
  };
};

// 能用高亮方法，返回匹配高亮字段与数量
export const getREGHeightObjText = (strTemp: any, keyWord: string) => {
  // 使用正则表达式的全局搜索标志match方法,计算高亮后数据与个数
  const itemUpper = strTemp?.toUpperCase();
  if (itemUpper && itemUpper?.indexOf(keyWord?.toUpperCase()) !== -1) {
    const replaceWord = strTemp?.substr(itemUpper?.indexOf(keyWord?.toUpperCase()), keyWord.length);
    const replaceHtml = `<span style="color:#326ff0">${replaceWord}</span>`;
    const regExp = new RegExp(replaceWord, 'gi');

    return strTemp?.replace(regExp, replaceHtml);
  }
  return strTemp;
};

/** 排序--公共方法 */
export function sortFun(list: any, sortKey: string, sortType: string) {
  if (Array.isArray(list) && list?.length > 0) {
    list?.sort((a: any, b: any) => {
      if (sortType == 'desc') {
        return a[sortKey] - b[sortKey];
      }
      return b[sortKey] - a[sortKey];
    });
  }
  return list;
}
// 验证是否对象
export function spreadIfObject(value: any) {
  return Object.prototype.toString.call(value) === '[object Object]' ? value : {};
}

export const COMPARE_PCCONSOLE_RELEASE_DEFAULT = {
  date: ['1', '30'],
  week: ['1', '4'],
  month: ['1', '6'],
} as any;

// 动态添加IPLibrary子菜单
export function getIPLibrary(newList: IMenuNewItemType[], parentId: string) {
  const menuArr: IMenuNewItemType[] = [];
  newList?.forEach((newItem: IMenuNewItemType) => {
    // IPLibrary添加后端返回的子菜单，即动态添加子页面
    if (newItem?.parentMenuId && newItem?.parentMenuId === parentId) {
      const hasIdList = menuArr?.map((item: IMenuNewItemType) => item.id);
      if (!hasIdList?.includes(newItem?.id)) {
        menuArr.push({
          ...newItem,
          titleCN: newItem?.title,
          path: `IPLibrary/${newItem.id}`,
          env: ['pre', 'pro'],
        });
      }
    }
  });
  return menuArr;
}

// 获取DateTypeCur
export function getDateTypeCurByActiveSegment(activeSegment: string) {
  switch (activeSegment) {
    case 'pcu':
    case 'ampere_dau':
    case 'ampere_d1':
    case 'ampere_d7':
    case 'wishlists':
    case 'hoursWatched':
    case 'peackCCV':
    case 'airtime':
    case 'wishlists_total':
      return 'daily';
    case 'gsd_digitalUnits':
      return 'weekly';
    case 'digitalRevenue':
    case 'units':
    case 'retention_d1':
    case 'lifetime_player':
    case 'mau':
    case 'ampere_mau':
    case 'ampere_lifetime_users':
    case 'ampere_avg_monthly_playtime':
      return 'monthly';
    case 'source_steam__hourly_ccu':
      return 'hour';
  }
}

export const STREAMING_METRICS_VALUE_LIST = [
  'hourswatched',
  'peackccv',
  'avgccv',
  'airtime',
  'hoursWatched',
  'peackCCV',
  'avgCCV',
];

// 判断两个字符串数组是否相等
export const areArraysEqualOrdered = (arr1: any, arr2: any) => {
  return (
    arr1?.length === arr2?.length && arr1?.every((item: any, index: number) => item === arr2[index])
  );
};

export const parseJSONString = <T = any>(defaultVal: T, jsonStr?: string): T => {
  if (!jsonStr) return defaultVal;
  try {
    return JSON.parse(jsonStr) || defaultVal;
  } catch (exception) {
    return defaultVal;
  }
};
